import React, { useEffect, useMemo } from "react";
import CountUp from "react-countup";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import Loader from "../../Components/Common/Loader";
import { useDashboard } from "../../hooks/useDashboard";
import DashboardInsights from "./DashboardInsights";
import CategoriesStats from "./CategoriesStats";

const buildWidgets = (dashboard) => [
  {
    key: "users",
    // Excludes internal accounts (admins, committee, assistants): this
    // counter measures the platform's audience, not its operators.
    label: "Utilisateurs actifs",
    hint: "Hors comptes internes",
    value: dashboard?.nb_users || 0,
    icon: "ri-team-line",
    tone: "success",
  },
  {
    key: "articles",
    label: "Articles",
    value: dashboard?.nb_articles || 0,
    icon: "ri-article-line",
    tone: "primary",
  },
  {
    key: "articles-validated",
    label: "Articles validés",
    value: dashboard?.nb_articles_validated || 0,
    icon: "ri-checkbox-circle-line",
    tone: "info",
  },
  {
    key: "articles-pending",
    label: "Articles en attente",
    value: dashboard?.nb_articles_unvalidated || 0,
    icon: "ri-time-line",
    tone: "warning",
  },
];

const Dashboard = () => {
  useEffect(() => {
    document.title = "Le Conseiller du BTP | Tableau de bord";
  }, []);

  const dashboardQ = useDashboard();
  const widgets = useMemo(
    () => buildWidgets(dashboardQ.data),
    [dashboardQ.data]
  );
  const error = dashboardQ.error;
  const errorMessage =
    typeof error === "string" ? error : error?.message || "Erreur";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Tableau de bord" pageTitle="Tableau de bord" />

          {dashboardQ.isLoading ? (
            <Loader />
          ) : error ? (
            <Loader error={errorMessage} />
          ) : (
            <Row>
              {widgets.map((widget) => (
                <Col xl={3} md={6} key={widget.key}>
                  <Card className="card-animate">
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <p className="text-uppercase fw-medium text-muted mb-0">
                            {widget.label}
                          </p>
                          {widget.hint ? (
                            <p className="text-muted fs-11 mb-0">
                              {widget.hint}
                            </p>
                          ) : null}
                          <h2 className="mt-4 ff-secondary fw-semibold">
                            <CountUp
                              start={0}
                              end={widget.value}
                              duration={1}
                              separator=" "
                            />
                          </h2>
                        </div>
                        <div className="flex-shrink-0">
                          <div
                            className={`avatar-sm rounded-circle bg-${widget.tone}-subtle d-flex align-items-center justify-content-center`}
                          >
                            <i
                              className={`${widget.icon} fs-2 text-${widget.tone}`}
                            />
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
              <DashboardInsights dashboard={dashboardQ.data} />
              <CategoriesStats dashboard={dashboardQ.data} />
            </Row>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
